import React from 'react';
import { Modal, Button } from 'react-bootstrap';

/**
 * ConfirmModal — a single reusable confirmation/prompt dialog.
 *
 * Built on react-bootstrap's Modal, which EVERY theme already skins via
 * `.modal-content` (classic Metronic, terminal/global.scss, graphite/global.scss)
 * and themed `.btn-*` buttons — so it renders natively in all themes with no
 * per-theme props. Use it for any "are you sure?" or quick-input dialog instead
 * of hand-rolling a one-off modal.
 *
 * Pass an input/textarea as `body` to turn it into a prompt (e.g. rename).
 */
export interface ConfirmModalProps {
  show: boolean;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** primary = neutral/affirmative action; danger = destructive (red confirm). */
  variant?: 'primary' | 'danger';
  /** true while the confirm action is in flight — disables buttons + close. */
  busy?: boolean;
  /** disable the confirm button (e.g. empty required input). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  show,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => (
  <Modal show={show} onHide={() => !busy && onCancel()} centered>
    <Modal.Header closeButton={!busy}>
      <Modal.Title className="fs-5 fw-bold">{title}</Modal.Title>
    </Modal.Header>
    {body != null && <Modal.Body>{body}</Modal.Body>}
    <Modal.Footer>
      <Button variant="light" onClick={onCancel} disabled={busy}>
        {cancelLabel}
      </Button>
      <Button variant={variant} onClick={onConfirm} disabled={busy || confirmDisabled}>
        {busy ? 'Working…' : confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

export default ConfirmModal;
